import { FocusEvent, FunctionComponent, MouseEvent, useEffect, useState } from 'react'
import { AppButton, AppCol, AppInputNumber, AppRow } from '@/shared/ui'
import PropTypes from 'prop-types'
import { PencilSimpleIcon, TrashIcon, MinusIcon, PlusIcon } from '@phosphor-icons/react'

export enum NS_MODE {
  ADD = 'ADD',
  UPDATE = 'UPDATE'
}

type NumberSpinnerLabels = {
  [key: string]: string
}

const LABELS: NumberSpinnerLabels = {
  add: 'Add',
  ready: 'Ready'
}

export type NumberSpinnerProps = {
  className?: string
  mode: NS_MODE
  value: number
  of?: number
  min?: number
  max?: number
  um?: string
  edit?: boolean
  step?: number
  decimals?: number
  disabledButtonAddItem?: boolean
  showRemoveButton?: boolean
  showReadyButton?: boolean
  showRemoveButtonOnMin?: boolean
  showHoverButtons?: boolean
  editable?: boolean
  allowEditOnBlur?: boolean
  onAdd?: (value: number) => void
  onSubtract?: (value: number) => void
  onRemove?: () => void
  onBlur?: () => void
  onReady?: (value: number) => void
  onChange?: (value: number) => void
  copy?: (text: string) => string
}

export const NumberSpinner: FunctionComponent<NumberSpinnerProps> = ({
  mode = NS_MODE.ADD,
  className = '',
  value,
  of,
  min,
  max,
  um = '',
  edit = false,
  editable,
  step = 1,
  decimals = 2,
  disabledButtonAddItem = false,
  showRemoveButton = true,
  showRemoveButtonOnMin = false,
  showReadyButton = true,
  showHoverButtons = true,
  allowEditOnBlur,
  copy = (text: string): string => (LABELS[text] ? LABELS[text] : text),
  onAdd = () => {},
  onSubtract = () => {},
  onRemove = () => {},
  onBlur = () => {},
  onReady = () => {},
  onChange = () => {}
}): JSX.Element => {
  const [allowUpdate, setAllowUpdate] = useState(edit)
  const [currentValue, setCurrentValue] = useState(value)
  const [showButtons, setShowButtons] = useState(false)
  const [addTimer, setAddTimer] = useState<NodeJS.Timeout | null>(null)
  const [subtractTimer, setSubtractTimer] = useState<NodeJS.Timeout | null>(null)
  const [allowRemove, setAllowRemove] = useState(min !== undefined && min !== null && min === value)

  const roundValue = (value: number) => {
    const _currentValue = Math.round(value * Math.pow(10, decimals ?? 2)) / Math.pow(10, decimals ?? 2)
    setCurrentValue(_currentValue)
    return _currentValue
  }

  const add = (e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    if (e.type?.toLocaleLowerCase().includes('mousedown') && e.button === 0) {
      let _currentValue = currentValue
      const _add = () => {
        if (max !== undefined && max !== null && _currentValue + step > max) {
          return
        }

        if (Number.isInteger(step)) {
          setCurrentValue((_currentValue += step))
        } else {
          _currentValue += step
          _currentValue = roundValue(_currentValue)
        }
        onAdd(_currentValue)
        change(_currentValue)
      }
      _add()
      setAddTimer(setInterval(_add, 250))
    } else {
      if (addTimer) {
        clearInterval(addTimer)
      }
      setAddTimer(null)
    }
  }

  const subtract = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (e.type?.toLocaleLowerCase().includes('mousedown')) {
      let _currentValue = currentValue
      const _subtract = () => {
        if (min !== undefined && min !== null && _currentValue - step < min) {
          return
        }
        if (Number.isInteger(step)) {
          setCurrentValue((_currentValue -= step))
        } else {
          _currentValue -= step
          _currentValue = roundValue(_currentValue)
        }
        onSubtract(_currentValue)
        change(_currentValue)
      }
      _subtract()
      setSubtractTimer(setInterval(_subtract, 250))
    } else {
      if (subtractTimer) {
        clearInterval(subtractTimer)
      }
      setSubtractTimer(null)
    }
  }

  const change = (value: number) => {
    onChange(value)
    setTimeout(() => {
      // Prevents fake remove clicks.
      setAllowRemove(min !== undefined && min !== null && min === value)
    }, 150)
  }

  const ready = () => {
    setTimeout(() => {
      setAllowUpdate(false)
      onReady(currentValue)
      change(currentValue)
    }, 120)
  }

  const remove = () => {
    if ((allowRemove || !allowUpdate) && !subtractTimer) {
      onRemove()
    }
  }

  const onInputChange = (value: number) => {
    const _min = min ?? Number.MIN_VALUE
    const _max = max ?? Number.MAX_VALUE

    if (value >= _min && value <= _max) {
      setCurrentValue(value)
      change(value)
    }
  }

  const handleBlur = (e: FocusEvent) => {
    const { currentTarget, relatedTarget } = e
    if (!currentTarget.contains(relatedTarget as Node)) {
      if (!allowEditOnBlur) {
        setCurrentValue(value)
        setAllowUpdate(false)
        setShowButtons(false)
        setAllowRemove(min !== undefined && min !== null && min === value)
      } else {
        let _currentValue = currentValue
        if (!Number.isInteger(step)) {
          _currentValue = roundValue(_currentValue)
          change(_currentValue)
        }
      }

      onBlur()
    }
  }

  const shouldShowRemoveButton = () => {
    return !allowUpdate || (showRemoveButtonOnMin && allowRemove)
  }

  useEffect(() => {
    return () => {
      setAllowUpdate(false)
      setAddTimer(null)
      setSubtractTimer(null)
      setShowButtons(false)
    }
  }, [])

  useEffect(() => {
    setCurrentValue(value)
    setAllowRemove(min !== undefined && min !== null && min === value)
  }, [value])

  useEffect(() => {
    setAllowUpdate(edit)
  }, [edit])

  return (
    <AppRow className={`gungnir-number-spinner ${className}`} gutter={8} onBlur={handleBlur} wrap={false}>
      <AppCol
        className={`gungnir-number-spinner-container`}
        tabIndex={1}
        onMouseEnter={() => setShowButtons(true)}
        onMouseLeave={() => {
          if (!allowUpdate) setShowButtons(false)
        }}
      >
        <AppRow
          className='gungnir-ns-wrapper'
          justify={showButtons && showHoverButtons ? 'space-between' : 'center'}
          align='middle'
          gutter={8}
          wrap={false}
        >
          {showButtons && showHoverButtons && (
            <AppCol>
              {shouldShowRemoveButton() ? (
                showRemoveButton ? (
                  <AppButton icon={<TrashIcon />} onClick={remove} />
                ) : null
              ) : (
                <AppButton icon={<MinusIcon />} onMouseDown={subtract} onMouseUp={subtract} onMouseLeave={subtract} />
              )}
            </AppCol>
          )}
          <AppCol className='gungnir-ns-value'>
            {!editable || !allowUpdate ? (
              of ? (
                <span>
                  {`${currentValue}`}
                  {of ? (
                    <span className='gungnir-ns-of'>
                      {' '}
                      / {of} {um ?? ''}
                    </span>
                  ) : null}
                </span>
              ) : (
                `${currentValue} ${um ?? ''}`
              )
            ) : (
              <AppInputNumber
                className='gungnir-ns-input-value'
                value={currentValue}
                bordered={false}
                onChange={onInputChange}
                formatter={(value) => `${value} ${um ?? ''}`}
                parser={(value) => parseFloat((value ?? '').replace(` ${um}`, ''))}
                min={min}
                max={max}
                step={step}
                autoFocus
              />
            )}
          </AppCol>
          {showButtons && showHoverButtons && (
            <AppCol>
              {!allowUpdate ? (
                <AppButton icon={<PencilSimpleIcon />} onClick={() => setAllowUpdate(true)} />
              ) : (
                <AppButton
                  icon={<PlusIcon />}
                  onMouseDown={add}
                  onMouseUp={add}
                  onMouseLeave={add}
                  onMouseEnter={add}
                />
              )}
            </AppCol>
          )}
        </AppRow>
      </AppCol>
      {allowUpdate && showReadyButton ? (
        <AppCol>
          <AppButton variant='primary' onClick={ready} disabled={disabledButtonAddItem}>
            {copy(mode === NS_MODE.ADD ? 'add' : 'ready')}
          </AppButton>
        </AppCol>
      ) : null}
    </AppRow>
  )
}

NumberSpinner.propTypes = {
  mode: PropTypes.oneOf([NS_MODE.ADD, NS_MODE.UPDATE]).isRequired,
  className: PropTypes.string,
  value: PropTypes.number.isRequired,
  um: PropTypes.string,
  edit: PropTypes.bool,
  step: PropTypes.number
}

NumberSpinner.defaultProps = {
  mode: NS_MODE.ADD,
  className: '',
  um: '',
  edit: false,
  step: 1
}
